export const blogPosts = [
  {
    slug: 'como-organizar-suas-financas-pessoais',
    titulo: 'Como organizar suas finanças pessoais do zero',
    resumo:
      'Saber para onde vai cada real é o primeiro passo para uma vida financeira tranquila. Veja um guia prático para começar hoje.',
    categoria: 'Organização',
    tempoLeitura: '5 min',
    dataPublicacao: '2025-05-01',
    conteudo: `
## Por que organizar as finanças?

Muitas pessoas chegam ao fim do mês sem entender por que o dinheiro acabou. Não é falta de renda — na maioria dos casos, é falta de visibilidade. Quando você não sabe para onde vai cada real, pequenos gastos somam e criam um rombo invisível no seu orçamento.

A boa notícia é que organizar as finanças não exige planilhas complicadas nem conhecimento avançado em economia. Basta seguir alguns passos consistentes.

## Passo 1: Registre todas as suas receitas

Antes de falar em cortar gastos, você precisa saber exatamente quanto entra. Isso inclui:

- Salário líquido (já com descontos de INSS e IR)
- Aposentadoria ou pensão
- Renda extra: freelas, aluguéis, bicos

Muita gente subestima a própria renda porque mistura valores brutos com líquidos. Use sempre o valor que efetivamente cai na sua conta.

## Passo 2: Separe despesas fixas e variáveis

**Despesas fixas** são as que chegam todo mês com valor previsível: aluguel, condomínio, plano de saúde, internet. Você pode lançá-las uma vez e elas se repetem automaticamente.

**Despesas variáveis** são as que mudam de valor — ou que podem nem acontecer no mês: supermercado, remédios, presentes, consertos. Essas precisam de atenção constante.

Separar os dois tipos ajuda a entender o que é custo fixo (difícil de cortar no curto prazo) e o que é custo variável (onde você tem mais margem de manobra).

## Passo 3: Calcule o seu saldo

Receitas totais menos despesas totais = saldo do mês. Se o número for negativo, você está gastando mais do que ganha. Se for positivo, esse valor está disponível para poupança ou investimento.

Fazer esse cálculo uma vez não é suficiente — o objetivo é acompanhar mês a mês para identificar tendências. Um mês ruim pode ser exceção; três meses ruins seguidos indicam um padrão que precisa de correção.

## Passo 4: Defina pelo menos uma meta de poupança

Organização sem objetivo vira rotina sem propósito. Ter uma meta — mesmo pequena, como uma reserva de emergência de R$ 1.000 — dá direção ao esforço de controlar os gastos.

Calcule quanto você precisaria guardar por mês para chegar lá. Se a conta não fechar com o seu saldo atual, revise as despesas variáveis antes de desistir da meta.

## Passo 5: Revise mensalmente

Reserve 20 minutos no início ou no fim de cada mês para revisar o que aconteceu. Pergunte-se: minhas despesas ficaram dentro do esperado? Atingi minha meta de poupança? O que posso melhorar no próximo mês?

Esse hábito simples — revisar e ajustar — é o que separa quem fica organizado de quem abandonou o controle depois de duas semanas.

## Conclusão

Organizar as finanças não exige sacrifício extremo. Exige consistência. Comece registrando o que entra, depois o que sai, calcule o saldo e defina uma meta. Com o hábito de revisão mensal, você vai construindo clareza financeira sem precisar virar a vida de cabeça para baixo.
    `.trim(),
  },
  {
    slug: 'despesas-fixas-vs-variaveis',
    titulo: 'Despesas fixas vs. variáveis: entenda a diferença e controle melhor',
    resumo:
      'Saber classificar seus gastos é a base de qualquer orçamento eficiente. Descubra o que é fixo, o que é variável e como lidar com cada um.',
    categoria: 'Planejamento',
    tempoLeitura: '4 min',
    dataPublicacao: '2025-05-05',
    conteudo: `
## A diferença que muda tudo

Uma das confusões mais comuns em finanças pessoais é tratar todos os gastos da mesma forma. Quando você junta aluguel, supermercado e jantar fora na mesma categoria, perde a capacidade de agir de forma cirúrgica.

Entender a diferença entre despesas fixas e variáveis é o que permite reduzir gastos sem sofrer — porque você vai cortar no lugar certo.

## O que são despesas fixas?

Despesas fixas são aquelas que se repetem todo mês com valor igual (ou muito próximo). Você não precisa decidir se vai pagar — a cobrança chega automaticamente.

Exemplos comuns:
- Aluguel ou prestação da casa própria
- Condomínio
- Internet e telefone
- Plano de saúde
- Mensalidade escolar
- Streaming (Netflix, Spotify etc.)
- Parcelas fixas de empréstimos

A característica principal é que elas são **difíceis de eliminar no curto prazo**. Para reduzir o aluguel, você precisaria mudar de imóvel. Para cancelar o plano de saúde, precisaria ponderar os riscos. Por isso, quando o dinheiro aperta, as fixas costumam ser as últimas a serem cortadas.

## O que são despesas variáveis?

Despesas variáveis são as que mudam de mês para mês — em valor, em frequência, ou nas duas coisas.

Exemplos:
- Supermercado
- Farmácia e remédios pontuais
- Gasolina ou transporte por aplicativo
- Roupas e calçados
- Presentes
- Consultas médicas não cobertas pelo plano
- Manutenção do carro ou eletrodomésticos

Aqui está a sua **maior margem de manobra**. Não porque você vai parar de comer, mas porque há escolhas dentro de cada variável: ir ao mercado com lista ou sem lista faz diferença; escolher a farmácia mais barata para genéricos também.

## Existe um terceiro tipo: as fixas que variam

A conta de energia elétrica é tecnicamente recorrente (chega todo mês), mas o valor oscila. O mesmo vale para água e para gastos com saúde de quem toma remédios controlados com doses ajustadas.

Esses gastos merecem atenção especial porque criam uma falsa sensação de controle: você registra, mas o valor real surpreende.

A dica é usar a **média dos últimos três meses** como referência e guardar uma pequena reserva para os meses em que a conta vem mais alta — especialmente no verão, quando o ar-condicionado pesa na energia.

## Como usar essa classificação na prática

1. **Liste todas as fixas primeiro** — elas são o seu custo base mensal inevitável.
2. **Some as fixas e subtraia da renda** — o resultado é o que sobra para variáveis e poupança.
3. **Distribua o restante entre variáveis** — idealmente com uma reserva para imprevistos.
4. **Monitore as variáveis semanalmente** — elas são onde o controle é mais efetivo.

Quando o orçamento aperta, é nas variáveis que você vai encontrar espaço de manobra. E quando você quer aumentar a poupança, são as fixas que merecem uma revisão estratégica.

## Conclusão

Classificar certo é a base para agir certo. Fixas e variáveis pedem estratégias diferentes: as fixas exigem planejamento de médio prazo para reduzi-las; as variáveis pedem disciplina diária. Combine as duas abordagens e você terá um orçamento que funciona na realidade — não apenas no papel.
    `.trim(),
  },
  {
    slug: 'metas-de-poupanca-como-definir-e-alcancar',
    titulo: 'Metas de poupança: como definir objetivos e realmente alcançá-los',
    resumo:
      'Ter um objetivo claro é o que transforma poupança em resultado. Aprenda a definir metas realistas, calcular prazos e manter o foco.',
    categoria: 'Poupança',
    tempoLeitura: '6 min',
    dataPublicacao: '2025-05-09',
    conteudo: `
## Por que a maioria das pessoas não consegue poupar

A resposta não é falta de dinheiro — é falta de destino. Guardar dinheiro "para o futuro" é vago demais para motivar qualquer pessoa. O cérebro humano responde melhor a objetivos concretos: uma viagem específica, uma reforma no banheiro, uma reserva de emergência de seis meses.

Quando a meta tem nome, valor e prazo, o sacrifício de deixar de gastar hoje ganha um significado real.

## O que faz uma boa meta de poupança

Uma meta financeira eficaz tem três componentes:

**1. Valor alvo definido**
Quanto você precisa? Seja específico. "Uma viagem para a praia" não é uma meta — "R$ 4.000 para uma semana em Florianópolis em janeiro" é uma meta.

**2. Prazo concreto**
Quando você precisa do dinheiro? O prazo determina o ritmo. R$ 4.000 em 10 meses = R$ 400 por mês. Em 20 meses = R$ 200 por mês. O prazo é o principal botão de ajuste de uma meta.

**3. Ponto de partida claro**
Quanto você já tem guardado para esse objetivo? Se já tem R$ 1.000, só precisa guardar mais R$ 3.000. Ignorar o que já existe subestima o progresso e desmotiva.

## Como calcular o quanto guardar por mês

A fórmula básica:

> **Contribuição mensal = (Valor alvo − Valor atual) ÷ Número de meses**

Exemplo: meta de R$ 6.000, já tem R$ 500, prazo de 12 meses.
(6.000 − 500) ÷ 12 = **R$ 458,33 por mês**

Se esse valor não cabe no seu orçamento, você tem duas opções: aumentar o prazo ou reduzir o valor alvo.

Para metas de longo prazo com juros, a fórmula de PMT (pagamento mensal com rendimento) considera o crescimento do dinheiro investido — o que reduz a contribuição necessária. Mas para simplificar, comece com a fórmula linear acima.

## O papel do prazo na motivação

Prazos curtos criam urgência. Prazos longos criam abandono. A faixa ideal fica entre 3 e 24 meses para a maioria dos objetivos cotidianos.

Para metas muito longas (aposentadoria, por exemplo), a solução é quebrar em marcos: "guardar R$ 20.000 em 2 anos" funciona melhor do que "guardar R$ 200.000 em 20 anos", mesmo que o segundo seja o objetivo real.

## Estratégias para manter o foco

**Separe o dinheiro logo que receber.** Guardar o que sobra no fim do mês raramente funciona porque o fim do mês frequentemente não sobra nada. O ideal é transferir o valor da meta assim que a renda cair na conta.

**Visualize o progresso.** Ver que você está 40% do caminho percorrido é muito mais motivador do que saber que ainda faltam 60%. Use percentuais, barras de progresso, qualquer representação visual que torne o avanço tangível.

**Não use o dinheiro da meta para emergências.** Para isso existe a reserva de emergência — um objetivo separado, com liquidez, cujo único propósito é evitar que você mexa nas outras metas quando imprevistos aparecem.

**Ajuste sem culpa.** Se o prazo precisar ser estendido ou o valor precisar ser reduzido, faça isso conscientemente. É melhor adaptar a meta do que abandoná-la.

## Tipos de metas mais comuns

- **Reserva de emergência:** 3 a 6 meses de despesas mensais. É a meta mais importante para quem ainda não tem.
- **Grandes compras:** eletrodomésticos, móveis, eletrônicos. Evita parcelamentos e juros.
- **Viagens:** uma das mais motivadoras porque tem data definida naturalmente.
- **Reforma ou reforma parcial:** tem custo estimável e urgência relativa, bom para médio prazo.
- **Fundo para imprevistos específicos:** carro, saúde, manutenção da casa.

## Conclusão

Poupança sem meta é esforço sem direção. Defina o valor, estabeleça o prazo, acompanhe o progresso mensalmente e ajuste quando necessário. Uma meta bem definida transforma o ato de guardar dinheiro de sacrifício em conquista.
    `.trim(),
  },
  {
    slug: 'reserva-de-emergencia-quanto-e-como-formar',
    titulo: 'Reserva de emergência: quanto guardar e por onde começar',
    resumo:
      'A reserva de emergência é o alicerce de qualquer vida financeira saudável. Entenda quanto você precisa, onde guardar e como construir esse colchão sem sacrifícios extremos.',
    categoria: 'Segurança Financeira',
    tempoLeitura: '5 min',
    dataPublicacao: '2025-05-12',
    conteudo: `
## O que é e por que você precisa

A reserva de emergência é um dinheiro guardado exclusivamente para situações imprevistas: perda de emprego, doença, conserto urgente do carro ou da casa, despesas médicas não cobertas pelo plano de saúde.

Sem ela, qualquer imprevisto vira dívida. E dívida tem juros. E juros corroem o orçamento por meses. A reserva de emergência existe para proteger o seu orçamento corrente de choques externos.

## Quanto guardar?

A regra mais usada é ter entre **3 e 6 meses de despesas mensais** guardados. Se você gasta R$ 3.000 por mês, a reserva ideal fica entre R$ 9.000 e R$ 18.000.

Mas nem todo mundo chega lá de uma vez — e tudo bem. O objetivo é construir progressivamente:

| Situação | Meta sugerida |
|---|---|
| Emprego estável, dois provedores de renda na família | 3 meses |
| Emprego estável, único provedor | 4–5 meses |
| Autônomo, freelancer ou renda variável | 6 meses ou mais |
| Aposentado ou pensionista com renda fixa garantida | 3 meses |

Se você ainda não tem nada, comece com uma meta menor: **R$ 1.000**. É suficiente para cobrir a maioria dos imprevistos cotidianos e, psicologicamente, é muito mais motivador do que mirar diretamente em R$ 15.000.

## Onde guardar a reserva

O dinheiro da reserva de emergência precisa ter três características:

1. **Liquidez imediata** — você precisa poder resgatar em até um dia útil.
2. **Segurança** — não pode estar exposto ao risco de mercado.
3. **Rendimento mínimo** — deve pelo menos acompanhar a inflação para não perder poder de compra.

As opções mais indicadas no Brasil:

- **Conta rendimento/investimento do Nubank, Inter ou C6:** rende 100% do CDI com liquidez diária, sem taxa.
- **Tesouro Selic:** muito seguro, liquidez em D+1, rende próximo ao CDI.
- **CDB de liquidez diária com 100% do CDI ou mais:** oferecido por vários bancos digitais.

**Evite:** poupança tradicional (rende menos que o CDI na maioria dos cenários), fundos com carência, ações ou qualquer aplicação com volatilidade.

## Como construir sem comprometer o orçamento

**Estratégia 1 — valor fixo mensal:** defina um valor que cabe no seu orçamento e transfira automaticamente assim que receber. R$ 200 por mês chegam a R$ 2.400 em um ano.

**Estratégia 2 — percentual da renda:** guardar entre 5% e 10% da renda líquida todo mês é uma regra fácil de lembrar e se ajusta automaticamente quando a renda muda.

**Estratégia 3 — dinheiro "extra":** 13º salário, restituição do IR, bônus, vendas de itens que não usa mais — direcione parte ou todo esse dinheiro à reserva.

Combine as três estratégias se puder: o valor fixo garante consistência, o percentual cria hábito e o dinheiro extra acelera a construção.

## O erro mais comum: usar a reserva para gastos que não são emergência

Viagem de fim de ano não é emergência. Presente de aniversário não é emergência. Black Friday não é emergência.

A reserva existe para eventos **imprevistos e necessários**. Para gastos planejados — mesmo que grandes — crie metas separadas. Misturar os dois vai deixar você sempre com a reserva incompleta e sempre vulnerável a imprevistos reais.

## Quando a reserva está pronta, o que vem depois?

Com a reserva formada, você tem a liberdade de assumir riscos calculados com o restante do dinheiro: investir em renda variável, quitar dívidas mais agressivamente, ou direcionar para metas de médio e longo prazo com mais rentabilidade.

A reserva não é para crescer — é para proteger. O crescimento vem dos outros investimentos que ela torna possíveis.

## Conclusão

A reserva de emergência é chata de construir e emocionante de ter. Comece pequeno, seja consistente e mantenha esse dinheiro separado do restante. Quando o imprevisto chegar — e ele vai chegar — você vai agradecer ter tomado essa decisão.
    `.trim(),
  },
  {
    slug: 'orcamento-50-30-20-como-aplicar',
    titulo: 'O método 50/30/20: uma regra simples para distribuir seu dinheiro',
    resumo:
      'Uma das regras de orçamento mais populares do mundo divide a renda em três categorias. Veja como funciona, como adaptar para a realidade brasileira e quando ela não se aplica.',
    categoria: 'Orçamento',
    tempoLeitura: '5 min',
    dataPublicacao: '2025-05-15',
    conteudo: `
## O que é o método 50/30/20?

O método 50/30/20 foi popularizado pela senadora americana Elizabeth Warren no livro *All Your Worth* (2005) e se tornou uma das regras de orçamento mais recomendadas no mundo por uma razão simples: é fácil de lembrar e de aplicar.

A ideia é dividir a renda líquida em três grandes categorias:

- **50% para necessidades**
- **30% para desejos**
- **20% para poupança e dívidas**

Veja o que entra em cada uma.

## 50%: Necessidades

São os gastos essenciais — o que você precisaria pagar mesmo no mês mais difícil da sua vida.

Exemplos:
- Moradia (aluguel, prestação, condomínio, IPTU)
- Alimentação básica (supermercado, não restaurante)
- Transporte para o trabalho
- Plano de saúde
- Contas de serviços essenciais (energia, água, internet básica)
- Medicamentos de uso contínuo

Se as suas necessidades passam de 50% da renda, você tem um problema estrutural que não resolve com corte de gastos variáveis. Pode ser necessário buscar renda adicional, mudar para um imóvel mais barato ou renegociar contratos.

## 30%: Desejos

São gastos que melhoram a qualidade de vida, mas que você poderia eliminar sem ameaçar a sobrevivência.

Exemplos:
- Streaming (Netflix, Spotify, Disney+)
- Jantar fora ou delivery
- Roupas além do necessário
- Passeios, lazer, hobbies
- Academia
- Assinaturas diversas

Aqui é onde mora a maior parte das decisões de consumo do dia a dia. O limite de 30% não significa que você deve cortar tudo — significa que, somando todos esses gastos, o total não deveria ultrapassar esse percentual.

## 20%: Poupança e dívidas

Essa fatia é destinada a construir o futuro e a eliminar o passado.

- **Poupança e investimentos:** reserva de emergência, metas de médio prazo, aposentadoria.
- **Pagamento de dívidas:** parcelas além do mínimo do cartão, empréstimos, financiamentos.

A ordem de prioridade dentro dos 20% depende da sua situação: se você tem dívidas com juros altos (cartão de crédito, cheque especial), liquidá-las primeiro costuma ser matematicamente mais vantajoso do que investir. Depois de quitar as dívidas caras, o foco passa para a poupança.

## Como aplicar na prática

**Passo 1:** Calcule a sua renda líquida mensal (o que entra na conta, já com todos os descontos).

**Passo 2:** Calcule os três limites:
- Necessidades: renda × 0,50
- Desejos: renda × 0,30
- Poupança: renda × 0,20

**Passo 3:** Classifique todos os seus gastos nas três categorias e some cada uma.

**Passo 4:** Compare com os limites e identifique onde está desequilibrado.

## A realidade brasileira: 50/30/20 precisa de ajuste?

Para muitas famílias brasileiras — especialmente as de renda média-baixa — a fatia de 50% para necessidades já não cobre a realidade. Aluguel em capitais, transporte público deficiente que força o uso de carro, planos de saúde com preços elevados: tudo isso pressiona as necessidades para 60–70% da renda.

Nesse caso, adapte sem culpa:

- Se as necessidades consomem 65%, deixe os desejos em 20% e a poupança em 15%.
- Se a situação é ainda mais apertada, priorize: **primeiro a reserva de emergência, depois os desejos**.

O método 50/30/20 é um guia, não uma lei. O que importa é ter clareza sobre onde o dinheiro vai e escolher conscientemente os trade-offs.

## Quando o método não se aplica diretamente

- **Renda muito baixa:** quando a renda mal cobre necessidades, não há margem para desejos. O foco deve ser aumentar a renda.
- **Dívidas críticas:** se você tem dívidas com juros acima de 10% ao mês (cartão, cheque especial), reorganize temporariamente para 50/10/40 — aumentando a fatia da poupança/dívida até quitar.
- **Renda variável:** use o método sobre a média dos últimos seis meses ou sobre o valor mínimo esperado, não sobre o melhor mês.

## Conclusão

O 50/30/20 é valioso não pela precisão dos números, mas pela clareza que traz: você para de tratar todos os gastos como iguais e começa a reconhecer o que é necessidade, o que é escolha e o que é investimento no futuro. Essa distinção, por si só, já transforma a relação de qualquer pessoa com o dinheiro.
    `.trim(),
  },
];

export function getBlogPost(slug) {
  return blogPosts.find((p) => p.slug === slug) || null;
}
